import { AutoComplete, AutoCompleteProps } from '@wix/design-system'
import React, { forwardRef, useCallback, useMemo, useRef } from 'react'

/**
 * Category dropdown that stays open once opened — neither clicking the input
 * again nor clicking outside closes it.
 *
 * `popoverProps` is spread over AutoComplete's own popover handlers, so
 * supplying `onOpenChange` replaces the built-in one and lets us ignore every
 * close request. Selecting an option still closes the dropdown: AutoComplete
 * handles that on the instance, not through the popover.
 *
 * AutoComplete cannot be subclassed — `@wix/design-system` exports it wrapped in
 * `forwardRef`, so `extends AutoComplete` throws at module evaluation.
 */
export const AutoCompleteWithOverrides = forwardRef<AutoComplete, AutoCompleteProps>((props, ref) => {
  const instance = useRef<AutoComplete | null>(null)

  const setRef = useCallback(
    (autoComplete: AutoComplete | null) => {
      instance.current = autoComplete

      if (typeof ref === 'function') {
        ref(autoComplete)
      } else if (ref) {
        ref.current = autoComplete
      }
    },
    [ref],
  )

  const popoverProps = useMemo(
    () =>
      ({
        ...props.popoverProps,
        onOpenChange: (open: boolean) => {
          if (open) {
            instance.current?.showOptions()
          }
        },
      } as AutoCompleteProps['popoverProps']),
    [props.popoverProps],
  )

  return <AutoComplete {...props} ref={setRef} popoverProps={popoverProps} />
})
